import React, { FC, useMemo } from 'react';
import styles from '../journey-explorer.module.scss';
import {
	JourneyAttribute,
	JourneyFilterState,
	JourneyFlowLink,
	JourneyFlowResult,
	JourneyMeta,
} from '../../../../models/journey-explorer/journey-explorer';
import { GoalInfo, formatNumber, goalRatio } from '../journey-explorer-utils';
import { flowLinkSegment, isTimeable } from '../journey-segments';
import { DrawerView } from './explore-drawer';
import SegmentBreakdown from './segment-breakdown';
import TimeBetween from './time-between';
import CompareStats from './compare-stats';
import ExportCustomers from './export-customers';

interface FlowLinkPanelProps {
	flow: JourneyFlowResult;
	link: JourneyFlowLink;
	title: string;
	goal: GoalInfo | null;
	meta: JourneyMeta;
	filters: JourneyFilterState;
	onClose: () => void;
	onOpenDrawer: (view: DrawerView) => void;
	onKeep: (attribute: JourneyAttribute, value: string) => void;
	compareLabels: [string, string] | null;
}

const FlowLinkPanel: FC<FlowLinkPanelProps> = ({
	flow,
	link,
	title,
	goal,
	meta,
	filters,
	onClose,
	onOpenDrawer,
	onKeep,
	compareLabels,
}) => {
	const segment = useMemo(() => flowLinkSegment(flow, link), [flow, link]);
	const sourceCustomers = flow.nodes.find((node) => node.key === link.source)?.customers ?? 0;

	return (
		<div className={styles.panel_body}>
			<div className={styles.panel_header}>
				<span className={styles.panel_title}>{title}</span>
				<button type="button" className={styles.panel_close} aria-label="Close" onClick={onClose}>
					✕
				</button>
			</div>
			<div className={styles.panel_subtitle}>Customers who made this move</div>
			<div className={styles.panel_stats}>
				<div className={styles.panel_stat}>
					<div className={styles.panel_stat_label}>Customers</div>
					<div className={styles.panel_stat_value}>{formatNumber(link.customers)}</div>
					<div className={styles.panel_stat_caption}>
						{sourceCustomers ? ((link.customers / sourceCustomers) * 100).toFixed(0) : 0}% of the step
						before
					</div>
				</div>
				{goal && (
					<div className={styles.panel_stat}>
						<div className={styles.panel_stat_label}>Reached {goal.label}</div>
						<div className={`${styles.panel_stat_value} ${styles.goal_text}`}>
							{link.reached_pct.toFixed(0)}%
						</div>
						<div className={styles.panel_stat_caption}>
							{goalRatio(link.reached_pct, goal).toFixed(1)}× the {goal.rate.toFixed(1)}% average
						</div>
					</div>
				)}
			</div>
			{compareLabels && link.a && link.b && <CompareStats labels={compareLabels} a={link.a} b={link.b} />}
			{segment && (
				<>
					<div className={styles.panel_actions}>
						<button
							type="button"
							className={styles.panel_action}
							onClick={() => onOpenDrawer({ mode: 'customers', segment, title })}
						>
							See customers
						</button>
						<ExportCustomers filters={filters} segment={segment} customers={link.customers} />
					</div>
					{isTimeable(segment) && (
						<>
							<div className={styles.panel_section}>Time between these steps</div>
							<TimeBetween filters={filters} segment={segment} />
						</>
					)}
					<div className={styles.panel_section}>Who are they?</div>
					<SegmentBreakdown meta={meta} filters={filters} segment={segment} onKeep={onKeep} />
				</>
			)}
		</div>
	);
};

export default FlowLinkPanel;
